import { useEffect, useState, Fragment } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { getContrastTextColor, isLightColor, isUserLoggedIn, getUserData } from '@utils'
import {
    Facebook,
    Linkedin,
    Instagram,
    Folder,
    Globe,
    Mail,
    Bell,
    Check,
    CheckCircle,
    Copy,
    Tag,
    FileText,
    Image as ImageIcon,
    ArrowRight,
    Video,
    ExternalLink,
    Share2
} from 'react-feather'
import {
    Button,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    Input,
    Badge,
    Card,
    CardBody,
    CardText
} from 'reactstrap'
import '@styles/base/pages/page-auth.scss'
import './BrandProfile.scss'
import {
    getBrandDetailsAtRD,
    getFavoriteFolders,
    createFavoriteFolder,
    addToFavorite,
    toggleFollowBrand,
    nullResponse
} from '@src/redux/actions/rdDashboard'
import { profileLinks } from '@src/redux/actions/brandsDashboard'
import { getAppliedCampaignDetail } from '@src/views/rds/brandCampaigns/store/actions'
import { NULL } from '@src/redux/actionTypes'
import ImageViewer from '@src/views/components/ImageViewer'
import PdfViewer from '@src/views/components/PdfViewer'
import VideoPlayer from '@src/views/components/VideoPlayer'
import ShareProfileModal from '@src/views/components/ShareProfileModal'

const BrandProfile = () => {
    const [isSelectFoldersModalOpen, setIsSelectFoldersModalOpen] = useState(false)
    const [folderName, setFolderName] = useState('')
    const [selectedFolder, setSelectedFolder] = useState(null)
    const [folderNameError, setFolderNameError] = useState(false)
    const [isFolderCreating, setIsFolderCreating] = useState(false)
    const [isFoldersLoading, setIsFoldersLoading] = useState(false)
    const [isAddingToFavorite, setIsAddingToFavorite] = useState(false)

    const [isPageLoading, setIsPageLoading] = useState(false)
    const [brandDetails, setBrandDetails] = useState(null)
    const [appliedDetailModal, setAppliedDetailModal] = useState(false)
    const [imageViewer, setImageViewer] = useState({ show: false, src: '' })
    const [pdfViewer, setPdfViewer] = useState({ show: false, url: '', filename: '' })
    const [videoPlayer, setVideoPlayer] = useState({ show: false, url: '', filename: '' })

    const [activeNavTab, setActiveNavTab] = useState('Overview')
    const [activeResourceSubTab, setActiveResourceSubTab] = useState('learn')
    const [isFollowing, setIsFollowing] = useState(false)
    const [isFollowLoading, setIsFollowLoading] = useState(false)
    const [isShareModalOpen, setIsShareModalOpen] = useState(false)

    const rdDashboardStoreSlice = useSelector((store) => store.rd)
    const {
        rdRecommendedCampaignsDetailLoading,
        rdRecommendedCampaignsDetailFullFill
    } = useSelector((store) => store.rdCampaigns)
    const rdCampaignSelector = useSelector((store) => store.rdCampaigns)

    const dispatch = useDispatch()
    const history = useHistory()
    const { id: brandId } = useParams()

    const userData = getUserData()
    const userRole = userData?.role || userData?.user_role || userData?.role_name
    const isBrand = userRole === 'brand' || userRole === 'brand_user' || userRole === 'secondary_user' || userRole === 'Brand'
    const isAdmin = userRole === 'admin' || userRole === 'sub_admin' || userRole === 'Admin'
    const isRD = userRole === 'rd' || userRole === 'RD'

    useEffect(() => {
        if (!!rdDashboardStoreSlice?.brandProfileAtRd) {
            setIsPageLoading(false)
            const raw = rdDashboardStoreSlice?.brandProfileAtRd
            const details = {
                ...(raw?.data || raw),
                ...raw
            }
            setBrandDetails(details)
            const rawFollow = raw?.is_following !== undefined ? raw.is_following : details?.is_following
            if (rawFollow !== undefined) {
                setIsFollowing(Boolean(rawFollow))
            }
        }
        if (!!rdDashboardStoreSlice?.addToFavroiteRes) {
            setIsPageLoading(true)
            dispatch(nullResponse(NULL.RD_ADD_TO_FAVORITE))
            dispatch(getBrandDetailsAtRD(brandId))
            setIsAddingToFavorite(false)
            setIsSelectFoldersModalOpen(false)
        }
        if (!!rdDashboardStoreSlice?.favoriteFoldersRes) {
            setIsFoldersLoading(false)
        }
        if (!!rdDashboardStoreSlice?.createfavoriteFolderRes) {
            setFolderName('')
            setIsFolderCreating(false)
            dispatch(nullResponse(NULL.RD_GET_FOLDERS))
            dispatch(nullResponse(NULL.RD_CREATE_FOLDER))
            if (isRD) {
                dispatch(getFavoriteFolders(false))
            }
            setIsFoldersLoading(true)
        }
    }, [rdDashboardStoreSlice, isRD])

    const checkAuth = () => {
        const token = localStorage.getItem(`${process.env.REACT_APP_USERTOKEN}`) || localStorage.getItem('accessToken')
        const loggedIn = Boolean(isUserLoggedIn() || token)
        if (!loggedIn) {
            history.push('/login?target=web')
            return false
        }
        return true
    }

    useEffect(() => {
        if (!brandId) return
        setIsPageLoading(true)
        if (isRD) {
            dispatch(getFavoriteFolders(false))
        }
        dispatch(getBrandDetailsAtRD(brandId)).then((res) => {
            if (res) {
                const details = {
                    ...res.data,
                    ...res
                }
                setBrandDetails(details)
                if (details.is_following !== undefined) {
                    setIsFollowing(Boolean(details.is_following))
                }
            }
        }).catch(() => {
        }).finally(() => {
            setIsPageLoading(false)
        })
    }, [brandId, isRD, dispatch])

    const handleRedirectMessage = (user) => {
        if (!checkAuth()) return
        localStorage.setItem('selectedChatBrand', JSON.stringify(user))
        if (isBrand) {
            history.push(`/brands/apps/chat/chatBrands`)
        } else if (isAdmin) {
            history.push(`/dashboard/admin`)
        } else {
            history.push(`/rd/apps/chat/chatRd/${user?.id || user?.user_id}`)
        }
    }

    const handleCreateFolder = () => {
        if (!folderName) {
            setFolderNameError(true)
            return
        }
        setFolderNameError(false)
        setIsFolderCreating(true)
        dispatch(createFavoriteFolder({ folderName }))
    }

    const handleSelectFolder = (folder) => {
        setSelectedFolder(folder)
    }

    const handleAddToFavorite = (profile, isModal) => {
        if (!checkAuth()) return
        if (profile.is_favorite && !isModal) {
            setIsPageLoading(true)
            dispatch(addToFavorite({ id: brandDetails.id, folder_id: profile.folder_id, type: 'profile' }))
        }
        if (!profile.is_favorite) setIsSelectFoldersModalOpen(true)
        if (!profile.is_favorite && isModal) {
            setIsAddingToFavorite(true)
            dispatch(addToFavorite({ id: brandDetails.id, folder_id: selectedFolder.id, type: 'profile' }))
        }
    }

    const handleOpenAppliedModal = (campaign) => {
        if (campaign?.version) {
            history.push(`/rd/apply-on-campaign/${campaign?.id}/view`)
        } else {
            dispatch(getAppliedCampaignDetail(campaign?.id))
            setAppliedDetailModal(true)
        }
    }

    const handleCopyCode = (code, type = 'rd') => {
        if (navigator.clipboard && code) {
            navigator.clipboard.writeText(code)
            if (type === 'client') {
                setCopiedClientCode(true)
                setTimeout(() => setCopiedClientCode(false), 2500)
            } else {
                setCopiedRdCode(true)
                setTimeout(() => setCopiedRdCode(false), 2500)
            }
        }
    }

    const [copiedRdCode, setCopiedRdCode] = useState(false)
    const [copiedClientCode, setCopiedClientCode] = useState(false)

    const handleToggleFollow = async () => {
        if (!checkAuth()) return
        const targetId = brandDetails?.profile_id || brandDetails?.id || brandId
        if (!targetId || isFollowLoading) return

        setIsFollowLoading(true)

        try {
            const res = await dispatch(toggleFollowBrand(targetId))
            const rawFollow = res?.is_following !== undefined ? res.is_following : res?.data?.is_following
            if (rawFollow !== undefined) {
                const nextFollow = Boolean(rawFollow)
                setIsFollowing(nextFollow)
                setBrandDetails(prev => (prev ? { ...prev, is_following: nextFollow } : prev))
            } else {
                setIsFollowing(prev => !prev)
                setBrandDetails(prev => (prev ? { ...prev, is_following: !prev.is_following } : prev))
            }
        } catch (error) {
            console.error('Toggle follow error:', error)
        } finally {
            setIsFollowLoading(false)
        }
    }

    const handleTrackSocialClick = (link) => {
        if (link) {
            dispatch(profileLinks({
                id: brandId,
                link,
                type: 'social'
            }))
        }
    }

    const handleTrackWebClick = (link) => {
        if (link) {
            dispatch(profileLinks({
                id: brandId,
                link,
                type: 'web'
            }))
        }
    }

    const brandThemeColor = brandDetails?.brand_color || '#56B6FF'
    const brandSecondaryColor = brandDetails?.brand_secondary_color || ''
    const isPrimaryLight = isLightColor(brandThemeColor)
    const isSecondaryLight = brandSecondaryColor ? isLightColor(brandSecondaryColor) : false
    const secondaryTextColor = brandSecondaryColor ? getContrastTextColor(brandSecondaryColor) : undefined
    const secondaryBorder = isSecondaryLight ? '1px solid rgba(0, 0, 0, 0.15)' : undefined
    const secondaryBadgeStyle = (brandSecondaryColor && brandSecondaryColor.trim().toLowerCase() !== brandThemeColor.trim().toLowerCase()) ? {
        backgroundColor: brandSecondaryColor,
        color: secondaryTextColor,
        border: secondaryBorder
    } : undefined

    const specialDiets = brandDetails?.attributes?.special_diet || []
    const categories = brandDetails?.attributes?.category || []
    const values = brandDetails?.attributes?.values || []
    const discountCode = brandDetails?.discount_code || ''
    const clientDiscountCode = brandDetails?.client_discount_code || ''
    const discountDetails = brandDetails?.discount_details || ''
    const shopWebsiteUrl = brandDetails?.shop_website || ''
    const hasDiscountCodes = Boolean(discountCode || clientDiscountCode)

    return (
        <Fragment>
            {isPageLoading && (
                <div className="w-100 my-4 text-center">
                    <Spinner color="primary" />
                </div>
            )}
            {!isPageLoading && !brandDetails && (
                <div className="w-100 my-5 text-center py-5">
                    <h4 className="font-weight-bold text-muted mb-1">Brand Profile Not Found</h4>
                    <p className="text-muted mb-2">The brand profile you are looking for does not exist or could not be loaded.</p>
                    <Link to="/" className="btn btn-outline-primary rounded-pill px-2">
                        Go to Home
                    </Link>
                </div>
            )}
            {!isPageLoading && !!brandDetails && (
                <div className="brand-profile-figma-v2">
                    {/* Top Hero Banner */}
                    <div className={classnames("brand-hero-wrapper", { "is-light-theme": isPrimaryLight })} style={{ backgroundColor: brandThemeColor }}>
                        {/* Top action row */}
                        <div className="hero-top-row">
                            <div className="hero-identity">
                                <div className="brand-circle-logo">
                                    {brandDetails?.company_logo ? (
                                        <img src={brandDetails.company_logo} alt={brandDetails?.brand_name} />
                                    ) : (
                                        <span style={{ color: isPrimaryLight ? '#0f172a' : brandThemeColor }}>
                                            {(brandDetails?.brand_name || 'B').substring(0, 3)}
                                        </span>
                                    )}
                                </div>
                                <div className="brand-info">
                                    <h1 className="brand-name-heading">{brandDetails?.brand_name || ''}</h1>
                                    {!!(brandDetails?.tagline || brandDetails?.person_title) && (
                                        <p className="brand-tagline-text">
                                            {brandDetails?.tagline || brandDetails?.person_title}
                                        </p>
                                    )}
                                    <div className="brand-pill-tags">
                                        {categories.map((item, idx) => (
                                            <span
                                                className="pill-tag"
                                                key={`cat-${idx}`}
                                                style={secondaryBadgeStyle}
                                            >
                                                {item.value}
                                            </span>
                                        ))}
                                        {specialDiets.map((item, idx) => (
                                            <span
                                                className="pill-tag"
                                                key={`diet-${idx}`}
                                                style={secondaryBadgeStyle}
                                            >
                                                {item.value}
                                            </span>
                                        ))}
                                        {values.map((item, idx) => (
                                            <span
                                                className="pill-tag"
                                                key={`val-${idx}`}
                                                style={secondaryBadgeStyle}
                                            >
                                                {item.value}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="hero-action-btns">
                                <button
                                    type="button"
                                    className="btn-hero-share"
                                    onClick={() => setIsShareModalOpen(true)}
                                    title="Share Brand Profile"
                                >
                                    <Share2 size={14} /> Share
                                </button>
                                <button className="btn-hero-message" onClick={() => handleRedirectMessage(brandDetails)}>
                                    <Mail size={14} /> Message
                                </button>
                                <button
                                    className={`btn-hero-follow ${isFollowing ? 'following' : ''}`}
                                    onClick={handleToggleFollow}
                                    disabled={isFollowLoading}
                                >
                                    {isFollowLoading ? (
                                        <Spinner size="sm" style={{ width: '13px', height: '13px', borderWidth: '2px' }} />
                                    ) : isFollowing ? (
                                        <Check size={14} />
                                    ) : (
                                        <Bell size={14} />
                                    )}
                                    {isFollowing ? 'Following' : 'Follow'}
                                </button>
                            </div>
                        </div>

                        {/* Social icons row directly underneath the logo circle */}
                        {(brandDetails.instagram_url || brandDetails.facebook_url || brandDetails.twitter_url || brandDetails.tiktok_url || brandDetails.pinterest_url || brandDetails.linkedin_url || brandDetails.website) && (
                            <div className="hero-social-row">
                                {!!brandDetails.instagram_url && (
                                    <a
                                        href={brandDetails.instagram_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => handleTrackSocialClick(brandDetails.instagram_url)}
                                        title="Instagram"
                                    >
                                        <Instagram size={16} />
                                    </a>
                                )}
                                {!!brandDetails.facebook_url && (
                                    <a
                                        href={brandDetails.facebook_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => handleTrackSocialClick(brandDetails.facebook_url)}
                                        title="Facebook"
                                    >
                                        <Facebook size={16} />
                                    </a>
                                )}
                                {!!brandDetails.twitter_url && (
                                    <a
                                        href={brandDetails.twitter_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => handleTrackSocialClick(brandDetails.twitter_url)}
                                        title="X (Twitter)"
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                        </svg>
                                    </a>
                                )}
                                {!!brandDetails.tiktok_url && (
                                    <a
                                        href={brandDetails.tiktok_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => handleTrackSocialClick(brandDetails.tiktok_url)}
                                        title="TikTok"
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 3 15.68 6.34 6.34 0 0 0 9.34 22a6.34 6.34 0 0 0 6.34-6.32V8.71a8.21 8.21 0 0 0 4.81 1.55v-3.45a4.85 4.85 0 0 1-.9-.12z" />
                                        </svg>
                                    </a>
                                )}
                                {!!brandDetails.pinterest_url && (
                                    <a
                                        href={brandDetails.pinterest_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => handleTrackSocialClick(brandDetails.pinterest_url)}
                                        title="Pinterest"
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345-.09.375-.291 1.199-.334 1.357-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.546.535 6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" />
                                        </svg>
                                    </a>
                                )}
                                {!!brandDetails.linkedin_url && (
                                    <a
                                        href={brandDetails.linkedin_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => handleTrackSocialClick(brandDetails.linkedin_url)}
                                        title="LinkedIn"
                                    >
                                        <Linkedin size={16} />
                                    </a>
                                )}
                                {!!brandDetails.website && (
                                    <a
                                        href={brandDetails.website}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => handleTrackWebClick(brandDetails.website)}
                                        title="Website"
                                    >
                                        <Globe size={16} />
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Navigation Tabs directly inside the dark hero banner */}
                        <div className="hero-nav-tabs">
                            {['Overview', 'Programs', 'Assets', 'About'].map((tab) => (
                                <button
                                    key={tab}
                                    className={`hero-tab-item ${activeNavTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveNavTab(tab)}
                                >
                                    {tab}
                                    {activeNavTab === tab && <div className="active-indicator" />}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Main Content Area below Hero */}
                    <div className="brand-content-body">
                        {/* 1. ABOUT CARD */}
                        {(activeNavTab === 'Overview' || activeNavTab === 'About') && (
                            <div className="figma-card about-card">
                                <div className="card-header-label">ABOUT {brandDetails?.brand_name || 'BRAND'}</div>
                                {brandDetails?.about_company ? (
                                    <div
                                        className="about-body-text"
                                        dangerouslySetInnerHTML={{
                                            __html: brandDetails.about_company
                                        }}
                                    />
                                ) : (
                                    <div className="about-body-text text-muted" style={{ fontStyle: 'italic' }}>
                                        No company description provided yet.
                                    </div>
                                )}
                                <div className="detail-table">
                                    <div className="detail-row">
                                        <div className="row-label">Category</div>
                                        <div className="row-value">
                                            {categories.length > 0 ? categories.map(c => c.value).join(' · ') : '-'}
                                        </div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="row-label">Special diet</div>
                                        <div className="row-value">
                                            {specialDiets.length > 0 ? specialDiets.map(s => s.value).join(' · ') : '-'}
                                        </div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="row-label">Certifications</div>
                                        <div className="row-value">
                                            {values.length > 0 ? values.map(v => v.value).join(' · ') : '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 2. RESOURCES CARD */}
                        {(activeNavTab === 'Overview' || activeNavTab === 'Assets') && (
                            <div className="figma-card resources-card">
                                <div className="resources-subnav-row">
                                    <button
                                        className={`subnav-tab ${activeResourceSubTab === 'learn' ? 'active' : ''}`}
                                        onClick={() => setActiveResourceSubTab('learn')}
                                    >
                                        Learn about the brand
                                    </button>
                                    <button
                                        className={`subnav-tab ${activeResourceSubTab === 'practice' ? 'active' : ''}`}
                                        onClick={() => setActiveResourceSubTab('practice')}
                                    >
                                        Use in your practice
                                    </button>
                                </div>

                                <div className="resource-color-indicators">
                                    <div className="indicator-pill green">
                                        <span className="dot green"></span>
                                        <span>Brand education</span>
                                    </div>
                                    <div className="indicator-pill purple">
                                        <span className="dot purple"></span>
                                        <span>Practice resources</span>
                                    </div>
                                </div>

                                <div className="resources-four-grid">
                                    {brandDetails?.media && brandDetails.media.length > 0 ? (
                                        (() => {
                                            const filteredMedia = brandDetails.media.filter((media, idx) => {
                                                const isEducation = media?.category === 'education' || media?.resource_type === 'education' || idx % 2 === 0
                                                if (activeResourceSubTab === 'learn') return isEducation
                                                if (activeResourceSubTab === 'practice') return !isEducation
                                                return true
                                            })
                                            if (filteredMedia.length === 0) {
                                                return (
                                                    <div className="resources-empty-state-box">
                                                        <div className="empty-icon-bubble">
                                                            <FileText size={16} />
                                                        </div>
                                                        <div className="empty-text-wrap">
                                                            <span className="empty-heading">
                                                                {activeResourceSubTab === 'learn' ? 'No Brand Education resources uploaded yet.' : 'No Practice resources uploaded yet.'}
                                                            </span>
                                                            <span className="empty-desc">
                                                                {activeResourceSubTab === 'learn'
                                                                    ? 'Educational documents and research materials will appear here.'
                                                                    : 'Practice handouts and client guides will appear here.'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )
                                            }
                                            return filteredMedia.map((media, idx) => {
                                                const type = media?.image_type?.toLowerCase() || ''
                                                const isEducation = activeResourceSubTab === 'learn' ? true : activeResourceSubTab === 'practice' ? false : (idx % 2 === 0)
                                                return (
                                                    <div
                                                        className="resource-item-box"
                                                        key={idx}
                                                        onClick={() => {
                                                            if (type === 'pdf') {
                                                                setPdfViewer({ show: true, url: media.image_url, filename: media.product_image })
                                                            } else if (type === 'mp4') {
                                                                setVideoPlayer({ show: true, url: media.image_url, filename: media.product_image })
                                                            } else {
                                                                setImageViewer({ show: true, src: media.image_url })
                                                            }
                                                        }}
                                                    >
                                                        <div className="item-preview-top">
                                                            {type === 'pdf' ? (
                                                                <FileText size={28} />
                                                            ) : type === 'mp4' ? (
                                                                <Video size={28} />
                                                            ) : (
                                                                <ImageIcon size={28} />
                                                            )}
                                                        </div>
                                                        <div className="item-meta-bottom">
                                                            <div className="item-title text-truncate">{media.product_image || 'Resource Document'}</div>
                                                            <div className={`item-type ${isEducation ? 'education' : 'practice'}`}>
                                                                {isEducation ? 'Brand education' : 'Use in practice'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        })()
                                    ) : (
                                        <div className="resources-empty-state-box">
                                            <div className="empty-icon-bubble">
                                                <FileText size={16} />
                                            </div>
                                            <div className="empty-text-wrap">
                                                <span className="empty-heading">
                                                    {activeResourceSubTab === 'learn' ? 'No Brand Education resources uploaded yet.' : 'No Practice resources uploaded yet.'}
                                                </span>
                                                <span className="empty-desc">
                                                    {activeResourceSubTab === 'learn'
                                                        ? 'Educational documents and research materials will appear here.'
                                                        : 'Practice handouts and client guides will appear here.'}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 3. DISCOUNT BANNER */}
                        {hasDiscountCodes && (activeNavTab === 'Overview' || activeNavTab === 'Programs') && (
                            <div className={classnames("figma-discount-card", { "is-light-theme": isPrimaryLight })} style={{ backgroundColor: brandThemeColor }}>
                                <div className="discount-info-side">
                                    <div className="discount-icon-bubble">
                                        <Tag size={20} />
                                    </div>
                                    <div className="discount-details-column">
                                        <div className="discount-eyebrow">RD EXCLUSIVE & CLIENT CODES</div>
                                        <div className="discount-codes-wrapper">
                                            {!!discountCode && (
                                                <div className="discount-code-row">
                                                    <span className="discount-label">RD Code:</span>
                                                    <span className="discount-promocode">{discountCode}</span>
                                                    <button className="btn-code-copy" onClick={() => handleCopyCode(discountCode, 'rd')} title="Copy RD code">
                                                        {copiedRdCode ? <Check size={12} /> : <Copy size={12} />}
                                                        <span className="copy-btn-text">{copiedRdCode ? 'Copied' : 'Copy'}</span>
                                                    </button>
                                                </div>
                                            )}
                                            {!!clientDiscountCode && (
                                                <div className="discount-code-row">
                                                    <span className="discount-label">Client Code:</span>
                                                    <span className="discount-promocode">{clientDiscountCode}</span>
                                                    <button className="btn-code-copy" onClick={() => handleCopyCode(clientDiscountCode, 'client')} title="Copy client code">
                                                        {copiedClientCode ? <Check size={12} /> : <Copy size={12} />}
                                                        <span className="copy-btn-text">{copiedClientCode ? 'Copied' : 'Copy'}</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {!!discountDetails && (
                                            <div className="discount-validity">
                                                {discountDetails.includes('|') ? (
                                                    discountDetails.split('|').map((part, pIdx) => (
                                                        <div key={pIdx} className={pIdx > 0 ? "mt-25" : ""}>
                                                            {part.trim()}
                                                        </div>
                                                    ))
                                                ) : (
                                                    discountDetails
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {!!shopWebsiteUrl && (
                                    <a
                                        href={shopWebsiteUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={() => handleTrackWebClick(shopWebsiteUrl)}
                                        className="btn-shop-website"
                                        style={secondaryBadgeStyle}
                                    >
                                        <ExternalLink size={13} /> Shop Website
                                    </a>
                                )}
                            </div>
                        )}

                        {/* 4. OPEN PROGRAMS */}
                        {(activeNavTab === 'Overview' || activeNavTab === 'Programs') && (
                            <div className="open-programs-wrapper">
                                <div className="programs-super-label">OPEN PROGRAMS</div>
                                <div className="programs-two-grid">
                                    {brandDetails?.campaigns && brandDetails.campaigns.length > 0 ? (
                                        brandDetails.campaigns.map((camp, idx) => (
                                            <div className="program-item-card" key={idx}>
                                                <div>
                                                    <div className={`program-kicker ${idx % 2 === 0 ? 'ambassador' : 'paid'}`}>
                                                        {camp.opportunity_for || (idx % 2 === 0 ? 'AMBASSADOR PROGRAM' : 'PAID OPPORTUNITY')}
                                                    </div>
                                                    <h3 className="program-heading">{camp.campaign_title}</h3>
                                                    <div className="program-details-line">
                                                        {camp.brand_name} · Active outreach program
                                                    </div>
                                                </div>
                                                <div className="program-action-footer">
                                                    {isBrand ? (
                                                        <Link
                                                            to={camp?.version ? `/brands/campaign-applications/${camp?.id}` : `/dashboard/brands/campaign-detail/${camp?.id}`}
                                                            className={`apply-direct-link ${idx % 2 === 0 ? 'ambassador' : 'paid'}`}
                                                        >
                                                            View Campaign <ArrowRight size={14} />
                                                        </Link>
                                                    ) : isAdmin ? (
                                                        <Link
                                                            to="/dashboard/admin"
                                                            className={`apply-direct-link ${idx % 2 === 0 ? 'ambassador' : 'paid'}`}
                                                        >
                                                            View in Admin <ArrowRight size={14} />
                                                        </Link>
                                                    ) : camp.is_applied ? (
                                                        <Badge
                                                            color="light-success"
                                                            className="p-50 cursor-pointer"
                                                            onClick={() => handleOpenAppliedModal(camp)}
                                                        >
                                                            Applied <CheckCircle size={13} />
                                                        </Badge>
                                                    ) : (
                                                        <Link
                                                            to={{
                                                                pathname: camp?.version ? `/rd/apply-on-campaign/${camp?.id}` : `/dashboard/rd/campaign-application/${camp?.id}`,
                                                                state: {
                                                                    brandName: camp?.brand_name,
                                                                    campName: camp?.campaign_title,
                                                                    brandImage: camp?.brand_img,
                                                                    campDetail: camp?.campaign_details,
                                                                    formTitle: camp?.form_title,
                                                                    formAttributes: camp?.form_attributes,
                                                                    opportunityFor: camp?.opportunity_for,
                                                                    campId: camp?.id,
                                                                    campFormId: camp?.campaign_form_id,
                                                                    rdDetails: rdCampaignSelector?.allCampaignsListing?.rd_details
                                                                }
                                                            }}
                                                            className={`apply-direct-link ${idx % 2 === 0 ? 'ambassador' : 'paid'}`}
                                                        >
                                                            Apply inside RD Link <ArrowRight size={14} />
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="w-100 text-center py-3 px-2 border rounded" style={{ backgroundColor: '#fcfdfe', borderColor: '#e2e8f0', borderRadius: '10px' }}>
                                            <div className="d-inline-flex align-items-center justify-content-center mb-1" style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#f1f5f9', color: '#64748b' }}>
                                                <Tag size={22} />
                                            </div>
                                            <h6 className="font-weight-bold mb-50" style={{ color: '#1e293b', fontSize: '14px' }}>
                                                No Active Outreach Programs
                                            </h6>
                                            <p className="text-muted mb-1 mx-auto" style={{ fontSize: '12.5px', maxWidth: '440px' }}>
                                                {brandDetails?.brand_name || 'This brand'} does not have any active ambassador or paid opportunities right now. Follow this brand to receive email notifications when new programs launch.
                                            </p>
                                            <Link
                                                to="/rd/rd-brand-campaigns"
                                                className="btn btn-outline-primary rounded-pill d-inline-flex align-items-center justify-content-center"
                                                style={{
                                                    fontSize: '13.5px',
                                                    fontWeight: 600,
                                                    padding: '9px 24px',
                                                    minHeight: '38px',
                                                    gap: '8px'
                                                }}
                                            >
                                                <span>Browse All Live Opportunities</span>
                                                <ArrowRight size={15} className="flex-shrink-0" />
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 5. BOTTOM FOLLOW CARD */}
                        <div className="figma-card bottom-follow-card">
                            <div className="follow-text-block">
                                <div className="follow-main-title">Follow {brandDetails?.brand_name || 'this brand'} to get notified about new programs and updates</div>
                                <div className="follow-sub-desc">You won't be notified unless you follow · unfollow anytime</div>
                            </div>
                            <button
                                className={`btn-follow-pill ${isFollowing ? 'following' : ''}`}
                                onClick={handleToggleFollow}
                                disabled={isFollowLoading}
                            >
                                {isFollowLoading ? (
                                    <Spinner size="sm" style={{ width: '13px', height: '13px', borderWidth: '2px' }} />
                                ) : isFollowing ? (
                                    <Check size={14} />
                                ) : (
                                    <Bell size={14} />
                                )}
                                {isFollowing ? `Following ${brandDetails?.brand_name || 'AG1'}` : `Follow ${brandDetails?.brand_name || 'AG1'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* APPLIED CAMPAIGN DETAIL MODAL */}
            <Modal isOpen={appliedDetailModal} toggle={() => setAppliedDetailModal(!appliedDetailModal)} className="modal-dialog-centered modal-lg">
                <ModalHeader toggle={() => setAppliedDetailModal(!appliedDetailModal)}>Application Detail</ModalHeader>
                {rdRecommendedCampaignsDetailLoading && (
                    <div className="w-100 my-4 text-center">
                        <Spinner color="primary" />
                    </div>
                )}
                {!rdRecommendedCampaignsDetailLoading && (
                    <ModalBody style={{ minHeight: '50px' }}>
                        <CardText className="bg-light p-1 text-break">
                            <b>{rdRecommendedCampaignsDetailFullFill?.data?.campaign_title}</b>
                        </CardText>
                        <div className="card-text text-break pl-1 mb-2" style={{ whiteSpace: 'break-spaces', textAlign: 'justify' }}>
                            <div dangerouslySetInnerHTML={{ __html: rdRecommendedCampaignsDetailFullFill?.data?.campaign_details }}></div>
                        </div>
                        <CardText className="text-capitalize bg-light p-1">
                            <b>{rdRecommendedCampaignsDetailFullFill?.data?.opportunity_for}</b>
                        </CardText>
                        <CardText className="bg-light p-1">
                            <b>{rdRecommendedCampaignsDetailFullFill?.data?.form_title}</b>
                        </CardText>
                        {rdRecommendedCampaignsDetailFullFill?.data?.form_attributes?.length &&
                            rdRecommendedCampaignsDetailFullFill?.data?.form_attributes.map((dat, index) => (
                                <Fragment key={index}>
                                    <CardText className="p-1 text-break" style={{ whiteSpace: 'break-spaces', textAlign: 'justify' }}>
                                        <b>Question #{index + 1}</b>
                                        <br />
                                        {dat?.question}
                                    </CardText>
                                    <CardText className="p-1 text-break" style={{ whiteSpace: 'break-spaces', textAlign: 'justify' }}>
                                        <b>Answer</b><br />
                                        {dat?.answer}
                                    </CardText>
                                </Fragment>
                            ))}
                    </ModalBody>
                )}
                <ModalFooter>
                    <Button.Ripple type="button" color="primary" onClick={() => setAppliedDetailModal(!appliedDetailModal)}>
                        Close
                    </Button.Ripple>
                </ModalFooter>
            </Modal>

            {/* SELECT FOLDERS MODAL */}
            <Modal scrollable isOpen={isSelectFoldersModalOpen} className="modal-dialog-centered">
                <ModalHeader toggle={() => setIsSelectFoldersModalOpen(false)}>Select Folder</ModalHeader>
                <ModalBody className="py-2" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    {isFoldersLoading && (<div className="w-100 my-4 text-center"><Spinner color="primary" /></div>)}
                    {!isFoldersLoading && (
                        <Fragment>
                            {rdDashboardStoreSlice?.favoriteFoldersRes?.data.map(item => (
                                <Card className={classnames({
                                    'ecommerce-card': true,
                                    'mb-1': true,
                                    'border-1px-white': selectedFolder?.id !== item.id,
                                    'cursor-pointer': true,
                                    'border-1px-purple': selectedFolder?.id === item.id
                                })} key={item.folder_name} onClick={() => handleSelectFolder(item)}>
                                    <CardBody className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <Folder className="mr-1 text-secondary" size={24} />
                                            <h5 className="mb-0">{item.folder_name}</h5>
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </Fragment>
                    )}

                    <p className="text-center mt-1">or</p>
                    <div className="form-group">
                        <Label htmlFor="folderName">Folder Name<span className="is-invalid">*</span></Label>
                        <Input
                            type="text"
                            onChange={(e) => {
                                setFolderName(e.target.value)
                                setFolderNameError(false)
                            }}
                            value={folderName}
                            id="folderName"
                            className={classnames({ 'is-invalid': folderNameError })}
                        />
                        {folderNameError && <div className="validation-err text-danger">This field is required</div>}
                    </div>
                    <div className="text-center">
                        <Button color="primary" outline disabled={isFolderCreating} onClick={handleCreateFolder}>
                            {isFolderCreating ? 'Creating...' : 'Create Folder'}
                        </Button>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" hidden={isAddingToFavorite} onClick={() => setIsSelectFoldersModalOpen(!isSelectFoldersModalOpen)}>
                        Cancel
                    </Button>
                    <Button color="primary" disabled={isAddingToFavorite} onClick={() => handleAddToFavorite(brandDetails, true)}>
                        {isAddingToFavorite ? 'Adding...' : 'Add to Favorite'}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* MEDIA VIEWERS */}
            {imageViewer.show && (
                <ImageViewer
                    show={imageViewer.show}
                    hide={() => setImageViewer({ show: false, src: '' })}
                    images={[{ src: imageViewer.src || '' }]}
                />
            )}
            {pdfViewer.show && (
                <PdfViewer
                    show={pdfViewer.show}
                    url={pdfViewer.url}
                    filename={pdfViewer.filename}
                    hide={() => setPdfViewer({ show: false, url: '', filename: '' })}
                />
            )}
            {videoPlayer.show && (
                <VideoPlayer
                    show={videoPlayer.show}
                    url={videoPlayer.url}
                    filename={videoPlayer.filename}
                    hide={() => setVideoPlayer({ show: false, url: '', filename: '' })}
                />
            )}

            {/* SHARE PROFILE MODAL */}
            <ShareProfileModal
                isOpen={isShareModalOpen}
                toggle={() => setIsShareModalOpen(!isShareModalOpen)}
                brandData={brandDetails}
            />
        </Fragment>
    )
}

export default BrandProfile
